import React, { useState, useMemo, useEffect } from "react";
import {
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Collapse,
  IconButton,
  Box,
  Typography,
} from "@mui/material";
import { Delete, Edit, ExpandLess, ExpandMore } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTableHeadCell from "lib/Tables/PivotTable/DataTableHeadCell";


export interface OcrolusColumn {
  Header: string;
  accessor: string;
  width?: string | number;
  align?: any;
  required?: any;
  Cell?: (args: { row: { original: any } }) => React.ReactNode;
  input ?: any;

}


interface OcrolusPivotTableProps {
  columns: OcrolusColumn[];
  data: any[];
  rowKey: string;

  selectedRows: any[];
  onSelectRow: (rowKey: any) => void;
  onSelectAll: () => void;

  onDelete?: (id: any) => void;
  onEdit?: (row: any) => void;

  onSort?: (columnKey: string) => void;
  sortedColumn?: string | null;
  sortDirection?: "asc" | "desc";

  getRowStyle?: (row: any) => number | string;
  isActionsDisabled?: (row: any) => boolean;

  renderTotal?: (rows: any[], key: string) => React.ReactNode;
  totalColumn?: string[];

  groupBy?: string;
  groupFormatter?: (value: any) => string;
  defaultExpanded?: boolean;
}


const OcrolusPivotTable: React.FC<OcrolusPivotTableProps> = ({
  columns,
  data,
  rowKey,
  selectedRows,
  onSelectRow,
  onSelectAll,
  onDelete,
  onEdit,
  onSort,
  sortedColumn,
  sortDirection = "asc",
  getRowStyle = () => 400,
  isActionsDisabled,
  renderTotal,
  totalColumn,
  groupBy,
  groupFormatter = (v) => String(v ?? "Unknown"),
  defaultExpanded = false,
}) => {
  const hasActions = Boolean(onDelete || onEdit);

  const sortedData = useMemo<any[]>(() => {
    if (!sortedColumn) return data;

    return [...data].sort((a, b) => {
      const av = a[sortedColumn];
      const bv = b[sortedColumn];

      if (av == null) return 1;
      if (bv == null) return -1;

      if (typeof av === "number" && typeof bv === "number") {
        return sortDirection === "asc" ? av - bv : bv - av;
      }

      return sortDirection === "asc"
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
  }, [data, sortedColumn, sortDirection]);

  const groupedData: Record<string, any[]> = useMemo(() => {
    if (!groupBy) return { All: sortedData };

    return sortedData.reduce<Record<string, any[]>>((acc, row) => {
      const key = groupFormatter(row[groupBy]);
      if (!acc[key]) acc[key] = [];
      acc[key].push(row);
      return acc;
    }, {});
  }, [sortedData, groupBy, groupFormatter]);

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {}
  );

  useEffect(() => {
    setExpandedGroups((prev) => {
      const next = { ...prev };
  
      Object.keys(groupedData).forEach((key) => {
        if (next[key] === undefined) {
          next[key] = defaultExpanded;
        }
      });
  
      return next;
    });
  }, [groupedData, defaultExpanded]);

  const allRowKeys = data.map((r) => r[rowKey]);
  const allSelected =
    allRowKeys.length > 0 &&
    allRowKeys.every((k) => selectedRows.includes(k));

  const colSpan = columns.length + 1 + (hasActions ? 1 : 0);


  const toggleGroup = (groupName: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };


  return (
    <TableContainer sx={{ width: "100%", boxShadow: "none" }}>
      <Table sx={{ tableLayout: "fixed", width: "100%" }}>
        <MDBox component="thead">
          <TableRow>
            <TableCell
              padding="checkbox"
              sx={{ width: 40, minWidth: 40, maxWidth: 40 }}
            >
              <Checkbox
                size="small"
                checked={allSelected}
                onChange={onSelectAll}
              />
            </TableCell>

            {columns.map((col) => (
              <DataTableHeadCell
                key={col.accessor}
                width={col.width}
                align={col.align || "left"}
                sorted={
                  sortedColumn === col.accessor
                    ? sortDirection === "asc"
                      ? "asce"
                      : "desc"
                    : "none"
                }
                onClick={() => onSort?.(col.accessor)}
              >
                {col.Header}
              </DataTableHeadCell>
            ))}

            {hasActions && (
              <DataTableHeadCell width="120px">
                Actions
              </DataTableHeadCell>
            )}
          </TableRow>
        </MDBox>

        <TableBody>
          {Object.entries(groupedData).map(([groupName, rows]) => {
            const expanded = expandedGroups[groupName];

            return (
              <React.Fragment key={groupName}>
                <TableRow>
                <TableCell colSpan={colSpan}  sx={{ px: 1,py:"1px", borderBottom: "none"}}>
                    <Box
                      display="flex"
                      alignItems="center"
                      onClick={() =>
                        setExpandedGroups((p) => ({
                          ...p,
                          [groupName]: !expanded,
                        }))
                      }
                      sx={{
                        cursor: "pointer",
                        padding: "8px",
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.08)",
                        },
                      }}
                    >
                      <IconButton size="small">
                        {expanded ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                      <Typography sx={{ ml: 1, fontWeight: "bold", fontSize: "15px" }}>
                        {groupName} ({rows.length} item{rows.length !== 1 ? "s" : ""})
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>

                <TableRow sx={{ padding: "-20px !important"}}>
                  <TableCell colSpan={colSpan} sx={{ padding: "0px", borderBottom: "none" }}>
                    <Collapse in={expanded} unmountOnExit>
                      <Table size="small" sx={{ tableLayout: "fixed" }}>
                        <TableBody>
                          {rows.map((row) => (
                            <TableRow key={row[rowKey]}>
                              <TableCell
                                padding="checkbox"
                                sx={{ width: 40 }}
                              >
                                <Checkbox
                                  size="small"
                                  checked={selectedRows.includes(row[rowKey])}
                                  onChange={() =>
                                    onSelectRow(row[rowKey])
                                  }
                                />
                              </TableCell>

                              {columns.map((col) => (
                                <TableCell
                                  key={col.accessor}
                                  sx={{
                                    padding: "8px",
                                    width: col.width,
                                    minWidth: col.width,
                                    maxWidth: col.width,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  <MDTypography
                                    noWrap
                                    fontSize="0.75rem"
                                    fontWeight={getRowStyle(row)}
                                  >
                                    {col.Cell
                                      ? col.Cell({
                                          row: { original: row },
                                        })
                                      : row[col.accessor]}
                                  </MDTypography>
                                </TableCell>
                              ))}

                              {hasActions && (
                                <TableCell sx={{ width: 120 }}>
                                  <MDBox display="flex" gap={0.5}>
                                    {onDelete && (
                                      <MDButton
                                        variant="text"
                                        color="error"
                                        startIcon={<Delete />}
                                        onClick={() =>
                                          onDelete(row[rowKey])
                                        }
                                        disabled={
                                          isActionsDisabled?.(row)
                                        }
                                      />
                                    )}
                                    {onEdit && (
                                      <MDButton
                                        variant="text"
                                        color="secondary"
                                        startIcon={<Edit />}
                                        onClick={() => onEdit(row)}
                                        disabled={
                                          isActionsDisabled?.(row)
                                        }
                                      />
                                    )}
                                  </MDBox>
                                </TableCell>
                              )}
                            </TableRow>
                          ))}

                        {renderTotal && (
                              <TableRow key="totals">
                                <TableCell></TableCell>
                                <TableCell align="left">
                                  <MDTypography fontSize={"0.85rem"} fontWeight={"bold"}>
                                    TOTAL:
                                  </MDTypography>
                                </TableCell>

                                {columns.map((column, index) => {
                                  if (index < 1) return;
                                  if (totalColumn?.includes(column.accessor)) {
                                    return (
                                      <TableCell
                                        key={column.accessor}
                                        sx={{ padding: "8px" }}
                                        align="left"
                                      >
                                        <MDTypography fontSize={"0.85rem"} fontWeight={"bold"}>
                                          {renderTotal(rows, column.accessor)}
                                        </MDTypography>
                                      </TableCell>
                                    );
                                  } else {
                                    return (
                                      <TableCell key={column.accessor} sx={{ padding: "8px" }}>
                                        <MDTypography
                                          fontSize={"0.75rem"}
                                          fontWeight={700}
                                        ></MDTypography>
                                      </TableCell>
                                    );
                                  }
                                })}
                              </TableRow>
                            )}

                        </TableBody>
                      </Table>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default OcrolusPivotTable;
